/**
 * Supervisor SOP Tab - Admin Panel
 * Digital checklists for shift start/end procedures
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ClipboardCheck, 
  RefreshCw,
  Clock,
  CheckCircle2,
  Circle,
  ChevronRight,
  Printer,
  Send,
  AlertTriangle,
  CheckCheck,
  Tablet,
  Printer as PrinterIcon,
  CreditCard,
  ChefHat,
  Users,
  ShoppingBag,
  Wallet,
  LogOut,
  Sparkles,
  BarChart3,
  History,
  X,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../utils/apiClient';

// Icon mapping for sections
const SECTION_ICONS = {
  'tablet': Tablet,
  'printer': PrinterIcon,
  'cash-register': CreditCard,
  'chef-hat': ChefHat,
  'users': Users,
  'check-circle': CheckCircle2,
  'shopping-bag': ShoppingBag,
  'wallet': Wallet,
  'log-out': LogOut,
  'sparkles': Sparkles,
  'bar-chart': BarChart3
};

export default function SupervisorSOPTab({ token }) {
  const [checklists, setChecklists] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [checklistDetails, setChecklistDetails] = useState(null);
  const [completedItems, setCompletedItems] = useState({});
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);

  // Fetch available checklists
  const fetchChecklists = useCallback(async () => {
    try {
      const response = await api.get('/sop/checklists');
      setChecklists(response.data.checklists || []);
    } catch (error) {
      console.error('Error fetching checklists:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch checklist details
  const fetchChecklistDetails = useCallback(async (checklistId) => {
    try {
      const response = await api.get(`/sop/checklists/${checklistId}`);
      setChecklistDetails(response.data);
      setCompletedItems({});
      setNotes('');
    } catch (error) {
      console.error('Error fetching checklist details:', error);
      toast.error('Failed to load checklist');
    }
  }, []);

  // Fetch submission history
  const fetchHistory = useCallback(async () => {
    try {
      const response = await api.get('/sop/submissions?days=30');
      setSubmissions(response.data.submissions || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }, [token]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/sop/stats?days=30');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchChecklists();
    fetchStats();
  }, [fetchChecklists, fetchStats]);

  // Handle item toggle
  const toggleItem = (itemId) => {
    setCompletedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Submit checklist
  const handleSubmit = async () => {
    if (!checklistDetails) return;
    
    setSubmitting(true);
    try {
      const completedItemsList = Object.entries(completedItems)
        .map(([item_id, completed]) => ({
          item_id,
          completed,
          notes: null
        }));
      
      const response = await api.post(`/sop/checklists/${selectedChecklist}/submit`, {
        checklist_id: selectedChecklist,
        completed_items: completedItemsList,
        notes: notes || null
      });
      
      const data = response.data;
      
      if (data.all_required_complete) {
        toast.success('Checklist completed successfully!');
      } else {
        toast.warning(`Submitted with ${data.missing_required_items?.length || 0} required items incomplete`);
      }
      setSelectedChecklist(null);
      setChecklistDetails(null);
      fetchStats();
    } catch (error) {
      console.error('Error submitting checklist:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit checklist');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate completion stats for current checklist
  const getCompletionStats = () => {
    if (!checklistDetails) return { completed: 0, total: 0, requiredComplete: 0, requiredTotal: 0 };
    
    let total = 0;
    let completed = 0;
    let requiredTotal = 0;
    let requiredComplete = 0;
    
    checklistDetails.sections.forEach(section => {
      section.items.forEach(item => {
        total++;
        if (completedItems[item.id]) completed++;
        if (item.required) {
          requiredTotal++;
          if (completedItems[item.id]) requiredComplete++;
        }
      });
    });
    
    return { completed, total, requiredComplete, requiredTotal };
  };

  const completionStats = getCompletionStats();
  const completionPercent = completionStats.total > 0 
    ? Math.round((completionStats.completed / completionStats.total) * 100) 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Supervisor SOP</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Standard Operating Procedures for shift management
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.open('/print-checklist', '_blank')}
            className="flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/50 text-green-700 dark:text-green-300 rounded-lg transition-colors"
            data-testid="print-checklists-btn"
          >
            <Download className="w-4 h-4" />
            Print Checklists
          </button>
          <button
            onClick={() => {
              setShowHistory(true);
              fetchHistory();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
          >
            <History className="w-4 h-4" />
            View History
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <ClipboardCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_submissions}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Submissions</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.full_completion_rate}%</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Full Completion</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.average_completion}%</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Avg Completion</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.period_days}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Days Tracked</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Checklist Selection or Active Checklist */}
      {!selectedChecklist ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {checklists.map((checklist) => (
            <motion.button
              key={checklist.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setSelectedChecklist(checklist.id);
                fetchChecklistDetails(checklist.id);
              }}
              className={`
                p-6 rounded-2xl border-2 text-left transition-all
                ${checklist.id === 'shift-start' 
                  ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-300 dark:border-green-700 hover:border-green-500'
                  : 'bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-orange-300 dark:border-orange-700 hover:border-orange-500'
                }
              `}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {checklist.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {checklist.description}
                  </p>
                </div>
                <ChevronRight className="w-6 h-6 text-gray-400" />
              </div>
              <div className="flex items-center gap-4 mt-4 text-sm">
                <span className={`flex items-center gap-1 ${checklist.id === 'shift-start' ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                  <Clock className="w-4 h-4" />
                  ~{checklist.estimated_time_minutes} min
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  {checklist.item_count} items
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  {checklist.section_count} sections
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      ) : checklistDetails ? (
        <div className="space-y-4">
          {/* Checklist Header */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setSelectedChecklist(null);
                    setChecklistDetails(null);
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {checklistDetails.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {completionStats.completed}/{completionStats.total} items completed
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {completionPercent}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {completionStats.requiredComplete}/{completionStats.requiredTotal} required
                </div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-green-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${completionPercent}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-4">
            {checklistDetails.sections.map((section, sIdx) => {
              const SectionIcon = SECTION_ICONS[section.icon] || ClipboardCheck;
              const sectionCompleted = section.items.filter(i => completedItems[i.id]).length;
              const sectionTotal = section.items.length;
              
              return (
                <div 
                  key={section.id}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white dark:bg-gray-600 rounded-lg">
                        <SectionIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      </div>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {section.name}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {sectionCompleted}/{sectionTotal}
                    </span>
                  </div>
                  
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {section.items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => toggleItem(item.id)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                      >
                        <div className={`
                          w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
                          ${completedItems[item.id] 
                            ? 'bg-green-500 border-green-500' 
                            : 'border-gray-300 dark:border-gray-600'}
                        `}>
                          {completedItems[item.id] && (
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <span className={`flex-1 text-sm ${
                          completedItems[item.id] 
                            ? 'text-gray-400 dark:text-gray-500 line-through' 
                            : 'text-gray-700 dark:text-gray-300'
                        }`}>
                          {item.text}
                        </span>
                        {item.required && !completedItems[item.id] && (
                          <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full">
                            Required
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Notes */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Additional Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any issues or notes for this shift..."
              rows={3}
              className="mt-2 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
            />
          </div>

          {/* Submit Button */}
          <div className="flex gap-3">
            <button
              onClick={() => {
                setSelectedChecklist(null);
                setChecklistDetails(null);
              }}
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Checklist
                </>
              )}
            </button>
          </div>

          {/* Warning if required items missing */}
          {completionStats.requiredComplete < completionStats.requiredTotal && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span>
                {completionStats.requiredTotal - completionStats.requiredComplete} required items still need to be completed
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
        </div>
      )}

      {/* History Modal */}
      <AnimatePresence>
        {showHistory && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowHistory(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="fixed inset-4 md:inset-10 bg-white dark:bg-gray-800 rounded-2xl z-50 flex flex-col overflow-hidden"
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Submission History</h3>
                <button
                  onClick={() => setShowHistory(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                {submissions.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <ClipboardCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No submissions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {submissions.map((sub) => (
                      <div
                        key={sub.id}
                        className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {sub.checklist_name}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            sub.all_required_complete 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          }`}>
                            {sub.completion_percentage}% Complete
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          <span>{sub.submitted_by_name}</span>
                          <span className="mx-2">·</span>
                          <span>{new Date(sub.submitted_at).toLocaleString()}</span>
                        </div>
                        {sub.notes && (
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 italic">
                            "{sub.notes}"
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
